import { useEffect, useMemo, useState } from "react";

import { groupBy } from "lodash";

import useFindPortfolioPlanAssignment from "@fartherfinance/frontend/api/Investment/hooks/useFindPortfolioPlanAssignment";
import {
  Filters,
  Pagination,
} from "@fartherfinance/frontend/api/Investment/request/findPortfolioPlanAssignment";
import { getPortfolioPlanById } from "@fartherfinance/frontend/api/Investment/request/getPortfolioPlanById";
import {
  PortfolioAssignment,
  PortfolioPlan,
} from "@fartherfinance/frontend/api/Investment/types";
import {
  ClientId,
  FartherAccountId,
  HookResult,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";

interface MiniPortfolioPlanForTable {
  name: string;
  updatedAt: Date;
  createdAt: Date;
  accountsAppliedCount: number;
  taxStatus: string;
  type: string;

  // not shown on table but good for debugging
  assignmentsAppliedCount: number;
  planId: PortfolioPlanId;
  accounts: FartherAccountId[];
}

type PortfolioPlanResponse = HookResult<MiniPortfolioPlanForTable[]>;

const infinitePagination: Pagination = {
  page: 1,
  pageSize: 100,
  orderBy: "createdAt desc",
};

export default function useFindPortfolioPlan(
  clientId: ClientId
): PortfolioPlanResponse {
  const [plans, setPlans] = useState<PortfolioPlanResponse>({
    isLoading: true,
    hasError: false,
    data: undefined,
  });

  const advisorAuth = useAdvisorRequestAuth();
  const clientAuth = useRequestAuth();
  const auth = clientAuth ?? advisorAuth;

  const filters: Filters = useMemo((): Filters => {
    return { clientId: clientId };
  }, [clientId]);

  // Get the portfolio plan assignments
  const portfolioPlanAssignments = useFindPortfolioPlanAssignment(
    filters,
    infinitePagination,
    auth
  );

  useEffect(() => {
    if (
      portfolioPlanAssignments.isLoading ||
      portfolioPlanAssignments.hasError
    ) {
      // do nothing till we have data
      return;
    }

    if (auth === null) {
      return;
    }

    // Create a list of portfolio plans (these should be distinct and unique)

    const planIds = portfolioPlanAssignments.data.data.map(
      (assignment) => assignment.portfolioPlanId
    );
    const uniquePlanIds = Array.from(new Set(planIds));

    // set loading state
    setPlans({
      isLoading: true,
      hasError: false,
      data: undefined,
    });

    // Get each of the plans, we must use a direct network request as hooks can't be looped
    Promise.all(
      uniquePlanIds.map((planId) =>
        getPortfolioPlanById(planId, { $expand: ["provider"] }, auth)
      )
    )
      .then((results) => {
        const plans = results.map((result) => result.data.plan);

        // Merge potentially multiple assignments into a single row per portfolio plan
        // groupedAssignments is of type Record<PortfolioPlanId, PortfolioPlanAssignment[]>
        const groupedAssignments = groupBy(
          portfolioPlanAssignments.data.data,
          (assignment) => assignment.portfolioPlanId
        );

        const miniPlans: MiniPortfolioPlanForTable[] = plans.map(
          portfolioPlanToMiniPlan(groupedAssignments)
        );

        // set success state
        setPlans({ isLoading: false, hasError: false, data: miniPlans });
      })
      .catch((e) => {
        // set error state
        setPlans({
          isLoading: false,
          hasError: true,
          error: e,
          data: undefined,
        });
      });
  }, [
    auth,
    portfolioPlanAssignments.data,
    portfolioPlanAssignments.hasError,
    portfolioPlanAssignments.isLoading,
  ]);

  if (portfolioPlanAssignments.isLoading || portfolioPlanAssignments.hasError) {
    return portfolioPlanAssignments;
  }

  if (
    portfolioPlanAssignments.data.pagination.totalCount >
    infinitePagination.pageSize
  ) {
    return {
      isLoading: false,
      hasError: true,
      error: new Error(
        `pageSize of (${infinitePagination.pageSize}) is lower than total count ${portfolioPlanAssignments.data.pagination.totalCount}`
      ),
      data: undefined,
    };
  }

  return plans;
}

function portfolioPlanToMiniPlan(
  groupedAssignments: Record<PortfolioPlanId, PortfolioAssignment[]>
) {
  return (plan: PortfolioPlan) => {
    const matchingAssignments = groupedAssignments[plan.portfolioPlanId] ?? [];

    const appliedAccounts = matchingAssignments
      .map((assignment) => assignment.accounts.length)
      .reduce((acc, curr) => acc + curr);

    let taxStatus: "Tax Exempt" | "Taxable" | "Any";
    if (plan.tags.includes("TaxExempt")) {
      taxStatus = "Tax Exempt";
    } else if (plan.tags.includes("Taxable")) {
      taxStatus = "Taxable";
    } else {
      taxStatus = "Any";
    }

    // gather all of the accounts per assignment into a single list and associate with the portfolio plan
    const accounts = matchingAssignments.flatMap((assignment) =>
      assignment.accounts.map((account) => account)
    );

    return {
      planId: plan.portfolioPlanId,
      name: plan.name,
      updatedAt: plan.updatedAt,
      createdAt: plan.createdAt,
      accountsAppliedCount: appliedAccounts,
      taxStatus: taxStatus,
      type: plan.provider ?? "Unknown",
      assignmentsAppliedCount: matchingAssignments.length,
      accounts: accounts,
    };
  };
}
